import { API_CONFIG } from 'config/api';
import { getCompanyIdFromToken } from '../../utils/jwt';

const LOAN_BASE_URL = API_CONFIG.LOAN.BASE_URL;

export interface LoanResponse {
  success: boolean;
  data?: any;
  message?: string;
}

export interface LoanStatistics {
  totalLoans: number;
  totalAmount: number;
  activeLoans: number;
  pendingLoans: number;
  overdueLoans: number;
  averageInterestRate: number;
  totalRemainingBalance: number;
  loanCountByType: Record<string, number>;
  loanCountByStatus: Record<string, number>;
  monthlyTrend: Array<{
    month: string;
    count: number;
    amount: number;
  }>;
}

export class LoanApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async getLoanStatistics(): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Use correct endpoint path with /v1 prefix
      const response = await fetch(`${LOAN_BASE_URL}/v1/loans/statistics`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch loan statistics: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get loan statistics API error:', error);
      throw new Error('Failed to fetch loan statistics');
    }
  }

  async getLoans(filters: {
    page?: number;
    limit?: number;
    status?: string;
    loanType?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<LoanResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.loanType) queryParams.append('loanType', filters.loanType);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`${LOAN_BASE_URL}${API_CONFIG.LOAN.ENDPOINTS.LOANS}?${queryParams}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get loans API error:', error);
      throw new Error('Failed to fetch loans');
    }
  }

  async getLoanApplications(filters: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<LoanResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`${LOAN_BASE_URL}${API_CONFIG.LOAN.ENDPOINTS.APPLICATIONS}?${queryParams}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get loan applications API error:', error);
      throw new Error('Failed to fetch loan applications');
    }
  }

  async getEmployeeLoans(employeeId: string): Promise<LoanResponse> {
    try {
      const response = await fetch(`${LOAN_BASE_URL}/customers/${employeeId}/loans`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get employee loans API error:', error);
      throw new Error('Failed to fetch employee loans');
    }
  }

  async getEmployeeLoanApplications(employeeId: string): Promise<LoanResponse> {
    try {
      const response = await fetch(`${LOAN_BASE_URL}/customers/${employeeId}/applications`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get employee loan applications API error:', error);
      throw new Error('Failed to fetch employee loan applications');
    }
  }

  async getPendingLoanApprovals(): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Extract companyId from JWT token instead of localStorage
      const companyId = getCompanyIdFromToken();
      if (!companyId) {
        throw new Error('Company ID not found in token');
      }

      // ✅ FIXED: Use endpoint without companyId in URL (backend uses JWT token)
      const response = await fetch(`${LOAN_BASE_URL}/v1/loans/applications/company/pending`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch pending loan approvals: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get pending loan approvals API error:', error);
      throw error instanceof Error ? error : new Error('Failed to fetch pending loan approvals');
    }
  }

  async getCompanyLoanApplications(filters?: {
    status?: string;
    customerId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Extract companyId from JWT token instead of localStorage
      const companyId = getCompanyIdFromToken();
      if (!companyId) {
        throw new Error('Company ID not found in token');
      }

      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.customerId) queryParams.append('customerId', filters.customerId);
      if (filters?.startDate) queryParams.append('startDate', filters.startDate);
      if (filters?.endDate) queryParams.append('endDate', filters.endDate);

      // ✅ FIXED: Use endpoint with companyId validation (backend validates URL param matches JWT)
      const queryString = queryParams.toString();
      const url = `${LOAN_BASE_URL}/v1/loans/applications/company/${companyId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch company loan applications: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get company loan applications API error:', error);
      throw error instanceof Error ? error : new Error('Failed to fetch company loan applications');
    }
  }

  async approveLoanApplication(applicationId: string, approvalData: {
    approvedAmount: number;
    interestRate: number;
    term: number;
    notes?: string;
  }): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Use correct endpoint path with /v1 prefix
      const response = await fetch(`${LOAN_BASE_URL}/v1/loans/applications/${applicationId}/employer-approve`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(approvalData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to approve loan application: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Approve loan application API error:', error);
      throw error instanceof Error ? error : new Error('Failed to approve loan application');
    }
  }

  async rejectLoanApplication(applicationId: string, reason: string): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Use correct endpoint path with /v1 prefix
      const response = await fetch(`${LOAN_BASE_URL}/v1/loans/applications/${applicationId}/employer-reject`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to reject loan application: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Reject loan application API error:', error);
      throw error instanceof Error ? error : new Error('Failed to reject loan application');
    }
  }

  async getCompanyLoans(filters?: {
    status?: string;
    customerId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Extract companyId from JWT token instead of localStorage
      const companyId = getCompanyIdFromToken();
      if (!companyId) {
        throw new Error('Company ID not found in token');
      }

      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.customerId) queryParams.append('customerId', filters.customerId);
      if (filters?.startDate) queryParams.append('startDate', filters.startDate);
      if (filters?.endDate) queryParams.append('endDate', filters.endDate);

      // ✅ FIXED: Use correct endpoint path - route is /company mounted at /api/v1/loans
      const queryString = queryParams.toString();
      const url = `${LOAN_BASE_URL}/v1/loans/company${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch company loans: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get company loans API error:', error);
      throw error instanceof Error ? error : new Error('Failed to fetch company loans');
    }
  }

  async updateLoan(loanId: string, updateData: {
    interestRate?: number;
    term?: number;
    monthlyPayment?: number;
    notes?: string;
  }): Promise<LoanResponse> {
    try {
      // ✅ FIXED: Use correct endpoint path - route is /loans/:loanId mounted at /api/v1/loans
      const response = await fetch(`${LOAN_BASE_URL}/v1/loans/loans/${loanId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update loan: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update loan API error:', error);
      throw error instanceof Error ? error : new Error('Failed to update loan');
    }
  }
}

export const loanApiService = new LoanApiService();
